import { useState, useCallback, useRef } from 'react';

interface UseOpenStateOptions {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  trackAnalytics: (action: string, data: Record<string, unknown>) => void;
  /**
   * Where focus goes on close when the element focused before opening is gone or was the
   * page itself (e.g. the launcher, which unmounts while the panel is open).
   */
  getFallbackFocus?: () => HTMLElement | null | undefined;
}

export function useOpenState({
  open,
  defaultOpen,
  onOpenChange,
  trackAnalytics,
  getFallbackFocus,
}: UseOpenStateOptions) {
  const initialOpen = open !== undefined ? open : !!defaultOpen;
  const [minimizedState, setMinimizedState] = useState(!initialOpen);
  const minimized = open !== undefined ? !open : minimizedState;
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const updateOpen = useCallback(
    (newOpen: boolean) => {
      if (newOpen) {
        previousFocusRef.current = document.activeElement as HTMLElement | null;
      }
      onOpenChange?.(newOpen);
      if (open === undefined) setMinimizedState(!newOpen);
      trackAnalytics('chat_toggle', { open: newOpen });
    },
    [onOpenChange, open, trackAnalytics]
  );

  const openChat = useCallback(() => updateOpen(true), [updateOpen]);
  const closeChat = useCallback(() => {
    updateOpen(false);
    // Restore focus when closing
    setTimeout(() => {
      const previous = previousFocusRef.current;
      const target =
        previous && previous.isConnected && previous !== document.body
          ? previous
          : getFallbackFocus?.();
      target?.focus();
    }, 0);
  }, [updateOpen, getFallbackFocus]);

  // A host modal will take focus; do not schedule launcher focus behind it.
  const closeChatForHandoff = useCallback(() => updateOpen(false), [updateOpen]);

  return { minimized, openChat, closeChat, closeChatForHandoff };
}
